import { Message } from "@/entities/message";

type FileInput = string | File;

export const getFileCategory = (
  input: FileInput
): "image" | "video" | "audio" | "other" => {
  if (typeof input === "string") {
    const lower = input.toLowerCase();

    if (lower.startsWith("data:image")) return "image";
    if (lower.startsWith("data:video")) return "video";
    if (lower.startsWith("data:audio")) return "audio";

    if (/\.(jpg|jpeg|png|gif|webp|bmp|svg|tiff|ico)$/i.test(lower))
      return "image";
    if (/\.(mp4|webm|mov|avi|mkv|flv|wmv|mpeg|mpg|3gp)$/i.test(lower))
      return "video";
    if (/\.(mp3|wav|ogg|m4a|aac|flac|wma|opus)$/i.test(lower)) return "audio";

    return "other";
  }

  // input is File
  const type = input.type.toLowerCase();
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";
  if (type.startsWith("audio/")) return "audio";
  return "other";
};

export const getSingleFileLabel = (input: FileInput): string => {
  if (typeof input === "string") {
    const lower = input.toLowerCase();

    if (
      /\.(jpg|jpeg|png|gif|webp|bmp|svg|tiff|ico)$/i.test(lower) ||
      lower.startsWith("data:image")
    ) {
      return "Изображение";
    }
    if (
      /\.(mp4|webm|mov|avi|mkv|flv|wmv|mpeg|mpg|3gp)$/i.test(lower) ||
      lower.startsWith("data:video")
    ) {
      return "Видео";
    }
    if (
      /\.(mp3|wav|ogg|m4a|aac|flac|wma|opus)$/i.test(lower) ||
      lower.startsWith("data:audio")
    ) {
      return "Аудиосообщение";
    }
    if (/\.pdf$/i.test(lower)) return "PDF-документ";
    if (/\.(doc|docx)$/i.test(lower)) return "Word-документ";
    if (/\.(xls|xlsx)$/i.test(lower)) return "Excel-таблица";
    if (/\.(ppt|pptx)$/i.test(lower)) return "Презентация";
    if (/\.(zip|rar|7z|tar|gz|bz2)$/i.test(lower)) return "Архив";

    return "Файл";
  }

  // input is File
  const name = input.name.toLowerCase();
  const type = input.type.toLowerCase();

  if (
    type.startsWith("image/") ||
    /\.(jpg|jpeg|png|gif|webp|bmp|svg|tiff|ico)$/i.test(name)
  ) {
    return "Изображение";
  }
  if (
    type.startsWith("video/") ||
    /\.(mp4|webm|mov|avi|mkv|flv|wmv|mpeg|mpg|3gp)$/i.test(name)
  ) {
    return "Видео";
  }
  if (
    type.startsWith("audio/") ||
    /\.(mp3|wav|ogg|m4a|aac|flac|wma|opus)$/i.test(name)
  ) {
    return "Аудиосообщение";
  }
  if (/\.pdf$/i.test(name)) return "PDF-документ";
  if (/\.(doc|docx)$/i.test(name)) return "Word-документ";
  if (/\.(xls|xlsx)$/i.test(name)) return "Excel-таблица";
  if (/\.(ppt|pptx)$/i.test(name)) return "Презентация";
  if (/\.(zip|rar|7z|tar|gz|bz2)$/i.test(name)) return "Архив";

  return "Файл";
};

export const getLastMessagePreview = (message?: Message): string => {
  if (!message) return "";

  if (message.text && message.text.trim().length > 0) {
    return message.text;
  }

  // Приоритет: fileUrls → files → filesBase64
  let relevantFiles: FileInput[] = [];

  if (message.fileUrls?.length) {
    relevantFiles = message.fileUrls.filter(Boolean);
  } else if (message.files?.length) {
    relevantFiles = message.files.filter(Boolean);
  } else if (message.filesBase64?.length) {
    relevantFiles = message.filesBase64.filter(Boolean);
  }

  if (relevantFiles.length === 0) return "";

  if (relevantFiles.length === 1) {
    return getSingleFileLabel(relevantFiles[0]);
  }

  const categories = relevantFiles.map(getFileCategory);
  const hasImage = categories.includes("image");
  const hasVideo = categories.includes("video");
  const hasAudio = categories.includes("audio");
  const hasOther = categories.includes("other");

  const count = relevantFiles.length;
  const plural = (base: string) =>
    count % 10 === 1 && count % 100 !== 11
      ? base
      : [2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)
      ? base + "а"
      : base + "ов";

  if (hasAudio && !hasImage && !hasVideo && !hasOther) {
    return `${count} аудиофайл${plural("")}`;
  }

  if (hasImage && hasVideo) {
    return `${count} медиафайл${plural("")}`;
  }

  if (hasImage && !hasVideo && !hasOther) {
    return `${count} фото`;
  }

  if (hasVideo && !hasImage && !hasOther) {
    return `${count} видео`;
  }

  return `${count} файл${plural("")}`;
};
